//@flow
import React,{Suspense,lazy,useEffect,useRef} from 'react';
import { Switch, Route, BrowserRouter as Router,Redirect  } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles';
import AppHeader from './components/top-level/AppHeader'
import AppFooter from './components/top-level/AppFooter'
import LeftNav from './components/top-level/LeftNav'
import { useStore } from './store/useStore'
import Spinner from './components/top-level/Spinner'
import Analytics from 'react-router-ga'
import { getUserSession } from './services/user-util'
import { refreshAppUserVersion,getAppVersion } from './services/useAppVersion'
import VersionChecker from './services/VersionChecker'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { LeftNavProvider } from './components/top-level/LeftNavContext'
import UpdateNotification from './components/other/UpdateNotification'

// Google Client ID
const GOOGLE_CLIENT_ID = "118887399306-lj5husledmi5k7okkm6ia9btmsjrpor7.apps.googleusercontent.com";

const Login = lazy(()=> import('./components/user/Login'))
const Profile = lazy(()=> import('./components/user/Profile'))
const Production = lazy(()=> import('./components/containers/Production'))
const NotFound = lazy(()=> import('./components/other/NotFound'))
const Qualification = lazy(()=> import('./components/containers/Qualification'))
const Training = lazy(()=> import('./components/containers/Training'))
const TaskReports = lazy(()=> import('./components/TaskReports'))
const WorkersTasksCounts = lazy(()=> import('./components/reports/WorkersTasksCounts'))
const WorkersTasksCountsByFormType = lazy(()=> import('./components/reports/WorkersTasksCountsByFormType'))
const WorkersXMLsCounts = lazy(()=> import('./components/reports/WorkersXMLsCounts'))
const MgmtCounts = lazy(()=> import('./components/reports/MgmtCounts'))
const BatchRuns = lazy(()=> import('./components/runs/BatchRuns'))
const BatchResultsets = lazy(()=> import('./components/runs/BatchResultsets'))
const AppUpdate = lazy(()=> import('./components/other/AppUpdate'))
const Tier1FiledropCounts = lazy(()=> import('./components/filedrop/Tier1FiledropCounts') )
const Tier2FiledropCounts = lazy(()=> import('./components/filedrop/Tier2FiledropCounts') )
const Tier3FiledropDetail = lazy(()=> import('./components/filedrop/Tier3FiledropDetail') )
const PageDataTable = lazy(()=> import('./components/generic/PageDataTable'))
const Vendors = lazy(()=> import('./components/vendors'))

const PrivateRoute = ({component: Component, authed, ...rest}: {component: React$ComponentType<any>, authed: boolean, [string]: any}) => {  
  return (    
       // $FlowFixMe[incompatible-indexer] - Route spread props
       <Route {...rest} render={(props) => authed === true 
         ? <Component {...props} /> 
         : <Redirect to={{pathname: '/login', state: {from: props.location }}} />} />  
  )
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    minHeight: '100vh',
    flexDirection: 'column',
    position: 'relative',
    backgroundColor: theme.palette.background.default,
  },
  appContent: {
    display: 'flex',
    flex: '1 1 auto',
  },
  contentShift: {
    marginLeft: 0, // Removed gap as LeftNav is now relative
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  content: {
    flexGrow: 1,
    padding: 0, // Removed padding to allow full-width footer
    marginTop: 64,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 'calc(100vh - 64px)',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  toolbar: {
    minHeight: 0, // Removed spacer as we use marginTop
  },
  mainContent: {
    flex: '1 1 auto',
    maxWidth: 1600,
    margin: '0 auto',
    width: '100%',
    padding: theme.spacing(3), // Moved padding here
  }
}));

function App(): React$Node {
  const classes = useStyles();
  const { dispatch, state } = useStore();
  const appVerAppUpdate = state.appVer && state.appVer.appUpdate;
  const loggedIn = state.user.loggedIn;
  const versionCheckerRef = useRef<?VersionChecker>(null);

  useEffect(()=>{
    getUserSession(dispatch)
  },[dispatch])

  useEffect(() => {
    if (loggedIn){
      // Initial version check
      getAppVersion(dispatch)
      refreshAppUserVersion()
      
      // Start periodic version checking
      if (!versionCheckerRef.current) {
        versionCheckerRef.current = new VersionChecker(dispatch);
        versionCheckerRef.current.start();
      }
    }
    
    // Cleanup on unmount or logout
    return () => {
      if (versionCheckerRef.current) {
        versionCheckerRef.current.stop();
        versionCheckerRef.current = null;
      }
    };
  },[dispatch,loggedIn])

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Router>
        <LeftNavProvider>
          <div className={classes.root}>
            <AppHeader />
            {loggedIn && <UpdateNotification />}
            
            <div className={classes.appContent}>
              {loggedIn && <LeftNav />}
              <main className={`${classes.content} ${loggedIn ? classes.contentShift : ''}`}>
                <div className={classes.toolbar} />
                <div className={classes.mainContent}>
                  <Analytics id="UA-93381200-7">
                    <Suspense fallback={<Spinner />}> 
                    <Switch>
                        <Route path="/login" exact={true} component={Login} />
                    <PrivateRoute authed={state.user.loggedIn} exact={true} path='/profile' component = {appVerAppUpdate?AppUpdate:Profile} />
                    <PrivateRoute authed={state.user.loggedIn} exact={true} path='/' component = {appVerAppUpdate?AppUpdate:Production} />
                    <PrivateRoute authed={state.user.loggedIn} exact={true} path='/qualification' component = {appVerAppUpdate?AppUpdate:Qualification} />
                    <PrivateRoute authed={state.user.loggedIn} exact={true} path='/training' component = {appVerAppUpdate?AppUpdate:Training} />
                    <PrivateRoute authed={state.user.loggedIn} exact={true} path='/taskReports' component = {appVerAppUpdate?AppUpdate:TaskReports} />
                    <PrivateRoute authed={state.user.loggedIn} exact={true} path='/reports/workersTasksCounts' component = {appVerAppUpdate?AppUpdate:WorkersTasksCounts} />
                    <PrivateRoute authed={state.user.loggedIn} exact={true} path='/reports/byFormType/workersTasksCounts' component = {appVerAppUpdate?AppUpdate:WorkersTasksCountsByFormType} />
                    <PrivateRoute authed={state.user.loggedIn} exact={true} path='/reports/WorkersXMLsCounts' component = {appVerAppUpdate?AppUpdate:WorkersXMLsCounts} />
                    <PrivateRoute authed={state.user.loggedIn} exact={true} path='/reports/mgmtCounts' component = {appVerAppUpdate?AppUpdate:MgmtCounts} />
                    <PrivateRoute authed={state.user.loggedIn} exact={true} path='/batchRuns' component = {appVerAppUpdate?AppUpdate:BatchRuns} />
                    <PrivateRoute authed={state.user.loggedIn} exact={true} path='/batchResultsets' component = {appVerAppUpdate?AppUpdate:BatchResultsets} />
                    <PrivateRoute authed={state.user.loggedIn} exact={true} path='/filedrop/tier1CountsReport' component = {appVerAppUpdate?AppUpdate:Tier1FiledropCounts} />
                    <PrivateRoute authed={state.user.loggedIn} exact={true} path='/filedrop/tier2CountsReport/:projectId' component = {appVerAppUpdate?AppUpdate:Tier2FiledropCounts} />
                    <PrivateRoute authed={state.user.loggedIn} exact={true} path='/filedrop/tier3FilesReport/:projectId/:fileDropCountId/:fileDropId' component = {appVerAppUpdate?AppUpdate:Tier3FiledropDetail} />
                    <PrivateRoute authed={state.user.loggedIn} exact={true} path='/pageDataTable/:pageDataId/:queryPath' component = {appVerAppUpdate?AppUpdate:PageDataTable} />
                    <PrivateRoute authed={state.user.loggedIn} exact={true} path='/vendors' component = {appVerAppUpdate?AppUpdate:Vendors} />
                    <Route >
                      <NotFound />
                    </Route>
                  </Switch>
                </Suspense>
              </Analytics>
                </div>
              </main>
            </div>
            <AppFooter />
          </div>
        </LeftNavProvider>
      </Router>
    </GoogleOAuthProvider>
  );
}

export default App;
